<script>
import { createNamespacedHelpers } from 'vuex';
import { Moment } from '@intelipost-js/secure-providers';
import { ScCard, ScTag, ScButton, ScTooltip } from '@intelipost-js/secure-components';
import formatPTCurrency from '../../../../shared/CurrencyFormatter';

const { mapState, mapActions } = createNamespacedHelpers('recon/billing');

export default {
  name: 'SingleBillingDetail',
  computed: {
    ...mapState(['loadingExtractDetail']),
    isLoadingExtractButton() {
      return !!this.loadingExtractDetail && this.loadingExtractDetail === this.detail.billingId;
    },
    valuesFirstRow() {
      return [
        { label: 'Vencimento', value: this.formatDates(this.detail.billingDueDate) },
        { label: 'Competência',
          value: `${this.formatDates(this.detail.billingDateRange.start)} - ${this.formatDates(this.detail.billingDateRange.end)}`,
        },
        { label: 'Total de pedidos', value: this.detail.shipmentOrdersCount },
        { label: 'Valor da fatura', value: formatPTCurrency(this.detail.billingTotalValue) },
      ];
    },
    valuesSecondRow() {
      return this.fillDivergencyRowValues(this.detail.outOfMarginDivergences);
    },
    valuesThirdRow() {
      return this.fillDivergencyRowValues(this.detail.insideMarginDivergences);
    },
  },
  methods: {
    ...mapActions(['exportBillingDetails']),
    getWarehouseDescription(billing) {
      const warehouseId = billing.billingWarehouseId;
      const warehouse = this.$store.state.warehousesDropdown.warehouses
        .find(w => w.id === warehouseId);

      if (!warehouseId || !warehouse) return 'CD não informado';
      return `${warehouse.code} - ${warehouse.official_name}`;
    },
    formatDates(date) {
      if (!date) return '';
      return Moment(date).format('DD.MM.YYYY');
    },
    fillDivergencyRowValues(divergencySource) {
      if (!divergencySource) return [];
      return [
        { label: 'Pedidos com divergência', value: divergencySource.shipmentOrdersCount },
        { label: 'Valor da divergência da fatura',
          value: formatPTCurrency(divergencySource.totalValue),
          description: 'O valor da divergência da fatura é a diferença do que foi cobrado a menos com o que foi cobrado a mais.' },
        { label: 'Valor cobrado a mais',
          value: `- ${formatPTCurrency(divergencySource.greatherThanTMS)}`,
          hasColor: true,
          description: 'Valor que demonstra que o valor cobrado pela transportadora é maior do que o valor esperado para a cobrança do frete.' },
        { label: 'Valor cobrado a menos',
          value: `+ ${formatPTCurrency(divergencySource.lowerThanTMS)}`,
          hasColor: true,
          description: 'Valor que demonstra que o valor cobrado pela transportadora é menor do que o valor esperado para a cobrança do frete.' },
      ];
    },
    exportDetail(divergentsOnly) {
      this.exportBillingDetails({ billingId: this.detail.billingId, divergentsOnly })
      .then(() => {
        this.$router.push({ name: 'billing-my-files-page', query: { activeTab: '1' } });
      });
    },
    getClassByDivergenceType(value) {
      const strValue = value.toString();
      if (strValue.startsWith('+')) {
        return 'is-positive-divergence';
      } else if (strValue.startsWith('-')) {
        return 'is-negative-divergence';
      }
      return false;
    },
  },
  props: {
    detail: {
      type: Object,
      required: true,
    },
  },
  components: { ScCard, ScTag, ScButton, ScTooltip },
};
</script>

<template>
  <sc-card class="detail-content">
    <template slot="body">
      <section class="detail-content__row">
        <p class="detail-content__row__title">Fatura: <span>{{ detail.billingName }}</span></p>
        <p class="detail-content__row__title">Centro de distribuição: <span>{{ getWarehouseDescription(detail) }}</span></p>
        <div class="detail-content__row__values">
          <div v-for="(v, i) in valuesFirstRow" :key="i">
            <p class="detail-content__row__values__label">{{ v.label }}</p>
            <p class="detail-content__row__values__value">{{ v.value }}</p>
          </div>
        </div>
      </section>
      <section class="detail-content__row" v-if="valuesSecondRow.length">
        <p class="detail-content__row__title">
          <span>Pedidos com divergências</span>
          <sc-tag size="is-medium" type="is-danger is-light" rounded>
            <i class="fas fa-info-circle"></i> Fora da margem
          </sc-tag>
        </p>
        <div class="detail-content__row__values">
          <div v-for="(v, i) in valuesSecondRow" :key="i">
            <p class="detail-content__row__values__label">{{ v.label }}
              <sc-tooltip v-if="v.description" type="is-dark" position="is-bottom" :label="v.description" multilined>
                <i class="far fa-question-circle"></i>
              </sc-tooltip>
            </p>
            <p class="detail-content__row__values__value"  v-if="v.hasColor" :class="getClassByDivergenceType(v.value)">{{ v.value }}</p>
            <p class="detail-content__row__values__value"  v-else="v.hasColor">{{ v.value }}</p>
          </div>
        </div>
      </section>
      <section class="detail-content__row" v-if="valuesThirdRow.length">
        <p class="detail-content__row__title">
          <span>Pedidos com divergências</span>
          <sc-tag size="is-medium" type="is-info is-light" rounded>
            <i class="fas fa-info-circle"></i> Dentro da margem
          </sc-tag>
        </p>
        <div class="detail-content__row__values">
          <div v-for="(v, i) in valuesThirdRow" :key="i">
            <p class="detail-content__row__values__label">{{ v.label }}
              <sc-tooltip v-if="v.description" type="is-dark" position="is-bottom" :label="v.description" multilined>
                <i class="far fa-question-circle"></i>
              </sc-tooltip>
            </p>
            <p class="detail-content__row__values__value" :class="getClassByDivergenceType(v.value)">{{ v.value }}</p>
          </div>
        </div>
      </section>
      <div class="detail-content__actions">
        <router-link class="button no-border" :to="{ name: 'billing-upload-page', query: { overwrite: detail.billingId } }">
          Reenviar fatura
        </router-link>
        <button class="button" :class="{'is-loading': isLoadingExtractButton}"
                @click="() => exportDetail(true)">
          Exportar pedidos com divergência
        </button>
        <button class="button" :class="{'is-loading': isLoadingExtractButton}"
                @click="() => exportDetail(false)">
          Exportar todos os pedidos
        </button>
      </div>
    </template>
  </sc-card>
</template>

<style scoped lang="scss">
@import "~@intelipost-js/secure-ui/src/scss/variables"; // eslint-disable-line

.detail-content {
  border-radius: 8px;

  &__row {
    margin-bottom: 24px;
    &:last-child {
      margin-bottom: 0;
    }

    &__title {
      font-size: 16px;
      color: $primary;

      span {
        font-weight: 700;
      }

      &:last-of-type {
        margin-bottom: 16px;
      }
    }

    &__values {
      display: flex;
      justify-content: space-between;

      div {
        flex: 1;
      }


      &__label {
        font-size: 12px;
        color: #39554A;
      }

      &__value {
        font-size: 14px;
        color: #010906;
      }
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;

    .button {
      font-weight: 700;
      margin-right: 16px;
      &:last-child {
        margin-right: 0;
      }
    }
  }

  .tag.is-danger {
    background-color: #FFF0EA;
    color: #E46329;
  }

  .tag.is-info {
    background-color: #EAF3FC;
    color: #3670D6;
  }
  .is-positive-divergence {
    color: #09A83D;
  }
  .is-negative-divergence {
    color: #C8323B;
  }

  .no-border {
    border: none;
  }
}
</style>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-recon/features/billing/pages/detail/components/single-billing-detail/SingleBillingDetail.vue